// frontend/src/utils/validate-opex.ts
import i18n from "@/i18n";
import { validateProjectData, ValidationError } from "@/utils/validate-project-data";
import type { ProjectFieldMetadata } from "@/types/ProjectFieldMetadata";

export type OpexValidationIssue = {
  rowIndex: number;
  field: "name" | "unit" | "valueStr" | "startStr" | "endStr" | "annualPercentStr";
  message: string;
  code?: "required" | "type" | "min" | "max" | "range" | "order" | "enum" | "pattern";
};

// IMPORTANT: align this list with your actual slice/DB literals.
// Updated to include "EUR_PER_KWP" per your slice.
const allowedUnits = [
  "EUR_PER_KWP",
  "EUR_CT_PER_KWH",
  "PERCENT_OF_SALES",
  "EUR",
  "PERCENT_OF_TOTAL_INVESTMENT",
] as const;


const isPercentUnit = (u: string | null | undefined) =>
  u === "PERCENT_OF_SALES" || u === "PERCENT_OF_TOTAL_INVESTMENT";

// normalize decimal comma to dot for numeric checks
const normalize = (s: string) => (s ?? "").toString().replace(",", ".");

// Build ad-hoc metadata per row based on unit
function buildRowMetadata(row: {
  unit?: string | null;
}): ProjectFieldMetadata[] {
  const base: ProjectFieldMetadata[] = [
    {
      field_name: "unit",
      original_label: "Unit",
      required: true,
      type: "string",
      allowed_values: [...allowedUnits],
    } as any,
    {
      field_name: "startStr",
      original_label: "Start year",
      required: false,    // allow SOP (blank)
      type: "number",
      pattern: "^$|^[0-9]+$", // integer if provided
    } as any,
    {
      field_name: "endStr",
      original_label: "End year",
      required: false,
      type: "number",
      pattern: "^$|^[0-9]+$", // integer if provided
    } as any,
    {
      field_name: "annualPercentStr",
      original_label: "Annual change (%)",
      required: false,
      type: "number",
      min_value: -100,
      max_value: 100,
      pattern: "^$|^-?[0-9]+(\\.[0-9]+)?$"
    } as any,
  ];

  const valueMeta: ProjectFieldMetadata =
    isPercentUnit(row.unit ?? null)
      ? {
          field_name: "valueStr",
          original_label: "Value (%)",
          required: true,
          type: "number",
          min_value: 0,
          max_value: 100,
        }
      : {
          field_name: "valueStr",
          original_label: "Value",
          required: true,
          type: "number",
          min_value: 0,
        };

  return [...base, valueMeta] as any;
}

// Map generic ValidationError -> row-scoped OpexValidationIssue
function mapErrorsToRow(rowIndex: number, errs: ValidationError[]): OpexValidationIssue[] {
  return errs.map((e) => ({
    rowIndex,
    field: e.field as OpexValidationIssue["field"],
    message: e.message,
  }));
}

/**
 * Validate OpEx rows using the same rule semantics as validate-project-data.ts
 * + extra cross-field checks (e.g., end >= start).
 * This does not replace backend validation.
 */
export function validateOpexRows(rows: Array<{
  name: string;
  unit?: string | null;
  valueStr: string;
  startStr: string;
  endStr: string;
  annualPercentStr: string;
}> | null | undefined): OpexValidationIssue[] {
  const issues: OpexValidationIssue[] = [];
  if (!rows || rows.length === 0) return issues;

  rows.forEach((row, idx) => {
    // Pre-normalize numeric-like strings so general validator's Number(...) works
    const fields = {
      unit: row.unit ?? "",
      valueStr: normalize(row.valueStr),
      startStr: normalize(row.startStr),
      endStr: normalize(row.endStr),
      annualPercentStr: normalize(row.annualPercentStr),
    };

    const metadata = buildRowMetadata(row);
    const { errors } = validateProjectData(fields, metadata);
    issues.push(...mapErrorsToRow(idx, errors));

    // Cross-field: allow SOP/EOP; validate only when provided
    const beginEmpty = fields.startStr.trim() === "";
    const endEmpty   = fields.endStr.trim() === "";

    const s = beginEmpty ? NaN : Number(fields.startStr);
    const e = endEmpty   ? NaN : Number(fields.endStr);

    const isInt = (x: number) => Number.isFinite(x) && Math.trunc(x) === x;

    // If provided, must be positive integer
    if (!beginEmpty) {
      if (!isInt(s) || s < 1) {
        issues.push({
          rowIndex: idx,
          field: "startStr",
          message: i18n.t("project.opex.validation.begin_year_positive_int"),
          code: "type",
        });
      }
    }
    if (!endEmpty) {
      if (!isInt(e) || e < 1) {
        issues.push({
          rowIndex: idx,
          field: "endStr",
          message: i18n.t("project.opex.validation.end_year_positive_int"),
          code: "type",
        });
      }
    }

    // If both provided and valid, enforce ordering
    if (isInt(s) && isInt(e) && e < s) {
      issues.push({
        rowIndex: idx,
        field: "endStr",
        message: i18n.t("project.opex.validation.end_ge_start"),
        code: "order",
      });
    }
    
  });

  return issues;
}